import { alphaSort, alphaSorter } from 'vet-bones/utils';

import {
  Mapping,
  MappingIdentifier,
  ModelConceptRelationship,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import { ModelSnapshot } from 'src/containers/ProjectDiagram/types/model';
import { isValidMapping } from 'src/containers/ProjectDiagram/utils/mapping/isValidMapping';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { RDFS_LABEL_IRI, XSD_STRING_IRI } from 'src/templates/queries/iris';
import { getVirtualGraphAttributeBinding } from 'src/utils/stringify/mappings/bindings';
import {
  attributeDataSort,
  getMappingHelpers,
  mappingFieldSort,
  stringifyLabel,
} from 'src/utils/stringify/mappings/utils';

export const stringifyVirtualGraphMappings = ({
  classMappings,
  model,
  relationshipMappings,
  dataNamespace,
  namespaces,
  query,
}: {
  classMappings: Mapping[];
  model: ModelSnapshot;
  relationshipMappings: RelationshipMapping[];
  dataNamespace: string;
  namespaces: Record<string, string>;
  query: string;
}) => {
  const { attributesByIri, labelsByIri } = model.modelData;

  // triples are triples in the `TO` block, while bindings are statements in the
  // WHERE block. Triples are condensed using a semicolon for each subject, so only
  // the first triple for each unit array has three parts, while the rest have two
  const bindings: string[] = [];
  const triplesByIdentifierVariable: Record<string, string[]> = {};

  const {
    abbreviate,
    getBindingAndVariable,
    getStringifiedLabel,
    getUniqueVariable,
  } = getMappingHelpers(labelsByIri, dataNamespace, namespaces);

  classMappings.forEach((mapping) => {
    if (!isValidMapping(mapping)(model)) {
      return;
    }

    const classIri = getModelConceptClassValue(mapping.to as string);
    const { binding, identifierVariable } = getBindingAndVariable(
      classIri,
      mapping.primaryIdentifier
    );

    // binding is null if duplicate
    if (binding) {
      bindings.push(binding);
    }

    if (!triplesByIdentifierVariable[identifierVariable]) {
      triplesByIdentifierVariable[identifierVariable] = [
        `${identifierVariable} a ${abbreviate(classIri)}`,
      ];
    }

    if (mapping.label) {
      // for SQL queries, a column name can be used as the label variable
      // (which the user already set as the "label" of a mapping link)
      const labelIri = abbreviate(RDFS_LABEL_IRI);

      if (triplesByIdentifierVariable[identifierVariable].length === 1) {
        triplesByIdentifierVariable[identifierVariable].push(
          `${labelIri} ?${mapping.label}`
        );
      }
    }

    Object.values(mapping.fields)
      .sort(mappingFieldSort)
      .forEach((field) => {
        if (!field.targets.length) {
          return;
        }

        field.targets.forEach((targetAttributeIri) => {
          const attribute = attributesByIri[targetAttributeIri];
          if (!attribute) {
            return;
          }

          const attributeIri = abbreviate(targetAttributeIri);
          const fieldLabel = stringifyLabel(field.label);

          Object.values(attribute.ranges)
            .sort(attributeDataSort)
            .forEach(({ value: rangeIri }) => {
              const rangeLabel = getStringifiedLabel(rangeIri);

              // we prepopulate the `usedVariables` record here
              // so that we avoid collisions with column names
              // that have similar names as our field variables
              getUniqueVariable(`?${field.value}`);

              const fieldVariable = getUniqueVariable(
                `?${fieldLabel}_${rangeLabel}_field`
              );
              const rangeAbbreviation = abbreviate(rangeIri);

              // For SQL mappings, column names can be used directly
              // as variables to get the stringified value from the table.
              // Therefore, values that are already expected to be strings do not
              // have to be binded
              if (rangeIri !== XSD_STRING_IRI) {
                const triple = `${attributeIri} ${fieldVariable}`;
                if (
                  !triplesByIdentifierVariable[identifierVariable].includes(
                    triple
                  )
                ) {
                  triplesByIdentifierVariable[identifierVariable].push(triple);
                  bindings.push(
                    getVirtualGraphAttributeBinding(
                      field.value,
                      rangeAbbreviation,
                      fieldVariable
                    )
                  );
                }
              } else {
                const triple = `${attributeIri} ?${field.value}`;
                if (
                  !triplesByIdentifierVariable[identifierVariable].includes(
                    triple
                  )
                ) {
                  triplesByIdentifierVariable[identifierVariable].push(triple);
                }
              }
            });
        });
      });
  });

  const relationshipsTriples = new Set<string>();
  relationshipMappings.forEach((relationshipMapping) => {
    const relationshipData = model.linkDataArray.find(
      (linkData) => linkData.value === relationshipMapping.relationshipIri
    ) as ModelConceptRelationship;

    if (!relationshipData || !relationshipData.from || !relationshipData.to) {
      return;
    }

    let fromClassIri: string | undefined;
    let fromPrimaryIdentifier: MappingIdentifier | undefined;
    if (relationshipMapping.from) {
      const sourceClassMapping = classMappings.find(
        (mapping) => mapping.key === relationshipMapping.from
      );
      if (sourceClassMapping?.to) {
        fromClassIri = getModelConceptClassValue(sourceClassMapping.to);
        fromPrimaryIdentifier = sourceClassMapping.primaryIdentifier;
      }
    } else if (
      relationshipMapping.fromClassIri &&
      relationshipMapping.fromPrimaryIdentifier
    ) {
      fromClassIri = relationshipMapping.fromClassIri;
      fromPrimaryIdentifier = relationshipMapping.fromPrimaryIdentifier;
    }

    let toClassIri: string | undefined;
    let toPrimaryIdentifier: MappingIdentifier | undefined;
    if (relationshipMapping.to) {
      const targetClassMapping = classMappings.find(
        (mapping) => mapping.key === relationshipMapping.to
      );
      if (targetClassMapping?.to) {
        toClassIri = getModelConceptClassValue(targetClassMapping.to);
        toPrimaryIdentifier = targetClassMapping.primaryIdentifier;
      }
    } else if (
      relationshipMapping.toClassIri &&
      relationshipMapping.toPrimaryIdentifier
    ) {
      toClassIri = relationshipMapping.toClassIri;
      toPrimaryIdentifier = relationshipMapping.toPrimaryIdentifier;
    }

    if (
      !fromClassIri ||
      !toClassIri ||
      !fromPrimaryIdentifier ||
      !toPrimaryIdentifier
    ) {
      return;
    }

    const { binding: fromClassBinding, identifierVariable: fromClassVariable } =
      getBindingAndVariable(fromClassIri, fromPrimaryIdentifier);
    const { binding: toClassBinding, identifierVariable: toClassVariable } =
      getBindingAndVariable(toClassIri, toPrimaryIdentifier);

    if (fromClassBinding) {
      bindings.push(fromClassBinding);
    }

    if (toClassBinding) {
      bindings.push(toClassBinding);
    }

    const triple = `${fromClassVariable} ${abbreviate(
      relationshipData.value
    )} ${toClassVariable}`;

    relationshipsTriples.add(triple);
  });

  return `MAPPING
FROM SQL {
  ${query}
}
TO {
  ${Object.values(triplesByIdentifierVariable)
    .sort(alphaSort(([key]) => key))
    .map((triples) => `${triples.join(' ;\n    ')} .`)
    .join('\n\n  ')}${
    relationshipsTriples.size
      ? `\n\n  ${Array.from(relationshipsTriples)
          .sort(alphaSorter)
          .join(' .\n\n  ')} .`
      : ''
  }
}
WHERE {
  ${bindings.join('\n  ')}
}`;
};
